import { htmlRules } from "@/shared/htmlRules";
import {
	blobSources,
	cancelDeferred,
	deferLoad,
	deferredSources,
} from "@client/carrier";
import {
	String,
	TextEncoder_encode,
	Object_keys,
	Object_defineProperty,
	Array_from,
	atob,
} from "@/shared/snapshot";
import { bytesToBase64, base64ToText } from "@/shared/util";
import { rewriteCss, unrewriteCss } from "@rewriters/css";
import { rewriteHtml, unrewriteHtml } from "@rewriters/html";
import { rewriteJs } from "@rewriters/js";
import { rewriteImportMap } from "@rewriters/importmap";
import { unrewriteUrl } from "@rewriters/url";
import { SCRAMJETCLIENT } from "@/symbols";
import { ScramjetClient } from "@client/index";
import {
	getScriptBlockTypeString,
	isHtmlMimeType,
	isModuleScriptType,
	isScriptType,
} from "@/shared/mime";
import { ForeignContext } from "@/shared/rewriters/html";

export function foreignContextForElement(
	client: ScramjetClient,
	element: Element
): ForeignContext {
	if (client.box.instanceof(element, "SVGElement")) return "svg";
	if (client.box.instanceof(element, "MathMLElement")) return "math";
	return "html";
}

// NOTE: NOT INCLUSIVE OF THE CURRENT ELEMENT
export function insideForeignContext(
	client: ScramjetClient,
	element: Element | null
): ForeignContext {
	let current: Element | null = element.parentElement;

	while (current) {
		const context = foreignContextForElement(client, current);
		if (context !== "html") return context;
		// EXPLICITLY an html context, don't go up further
		if (client.box.instanceof(current, "SVGForeignObjectElement"))
			return "html";
		current = current.parentElement;
	}

	return "html";
}

function scriptBlockTypeForElement(
	client: ScramjetClient,
	element: Element
): string {
	const hasType = client.natives.call(
		"Element.prototype.hasAttribute",
		element,
		"type"
	) as boolean;
	const hasLanguage = client.natives.call(
		"Element.prototype.hasAttribute",
		element,
		"language"
	) as boolean;
	const type = hasType
		? (client.natives.call(
				"Element.prototype.getAttribute",
				element,
				"type"
			) as string | null)
		: null;
	const language = hasLanguage
		? (client.natives.call(
				"Element.prototype.getAttribute",
				element,
				"language"
			) as string | null)
		: null;
	return getScriptBlockTypeString(type, language, hasType, hasLanguage);
}

// TODO: this is pretty bad. really this whole file sucks
function collectAttributeMap(
	client: ScramjetClient,
	element: Element,
	overrideName: string,
	overrideValue: unknown
) {
	const attrs: Record<string, string | undefined> = {};
	const attrNames =
		client.natives.call("Element.prototype.getAttributeNames", element) ?? [];

	for (const attrName of attrNames) {
		if (String(attrName).startsWith("scramjet-attr")) continue;
		const value = client.natives.call(
			"Element.prototype.getAttribute",
			element,
			attrName
		);
		attrs[String(attrName).toLowerCase()] =
			typeof value === "string" ? value : undefined;
	}

	attrs[String(overrideName).toLowerCase()] = String(overrideValue);
	return attrs;
}

export default function (client: ScramjetClient, self: typeof window) {
	const attrObject = {
		nonce: [self.HTMLElement],
		integrity: [self.HTMLScriptElement, self.HTMLLinkElement],
		csp: [self.HTMLIFrameElement],
		credentialless: [self.HTMLIFrameElement],
		src: [
			self.HTMLImageElement,
			self.HTMLMediaElement,
			self.HTMLIFrameElement,
			self.HTMLFrameElement,
			self.HTMLEmbedElement,
			self.HTMLScriptElement,
			self.HTMLSourceElement,
			// set by script, subtitles (Rumble's player) and image buttons loaded straight
			// from the site
			self.HTMLTrackElement,
			self.HTMLInputElement,
		],
		href: [self.HTMLAnchorElement, self.HTMLLinkElement, self.HTMLAreaElement],
		ping: [self.HTMLAnchorElement, self.HTMLAreaElement],
		data: [self.HTMLObjectElement],
		action: [self.HTMLFormElement],
		formaction: [self.HTMLButtonElement, self.HTMLInputElement],
		target: [
			self.HTMLAnchorElement,
			self.HTMLAreaElement,
			self.HTMLBaseElement,
			self.HTMLFormElement,
		],
		formtarget: [self.HTMLButtonElement, self.HTMLInputElement],
		srcdoc: [self.HTMLIFrameElement],
		poster: [self.HTMLVideoElement],
		imagesrcset: [self.HTMLLinkElement],
		srcset: [self.HTMLImageElement, self.HTMLSourceElement],
	};
	// Attributes whose property has another name.
	const propertyNames: Record<string, string> = {
		formaction: "formAction",
		formtarget: "formTarget",
		imagesrcset: "imageSrcset",
	};

	const urlinterfaces = [
		self.HTMLAnchorElement.prototype,
		self.HTMLAreaElement.prototype,
	];
	const originalhrefs = [
		client.natives.call(
			"Object.getOwnPropertyDescriptor",
			null,
			self.HTMLAnchorElement.prototype,
			"href"
		),
		client.natives.call(
			"Object.getOwnPropertyDescriptor",
			null,
			self.HTMLAreaElement.prototype,
			"href"
		),
	];

	const attrs = Object_keys(attrObject);

	for (const attr of attrs) {
		for (const element of attrObject[attr]) {
			const property = propertyNames[attr] ?? attr;
			const descriptor = client.natives.call(
				"Object.getOwnPropertyDescriptor",
				null,
				element.prototype,
				property
			);
			// Not every browser has every property; there is nothing to hook then.
			if (!descriptor?.get) continue;
			Object_defineProperty(element.prototype, property, {
				get() {
					// The nonce as the page's scripts read it to pass it on (Meta's ServerJS checks
					// it: "payload nonce validation failed"; Google's tags, webpack's loader).
					// The rewriting takes the attribute out, which left the property empty.
					if (attr === "nonce") {
						const kept = client.natives.call(
							"Element.prototype.getAttribute",
							this,
							"scramjet-attr-nonce"
						);
						if (kept !== null) return kept;
					}
					// fetched through a carrier and not here yet, or here as a blob (carrier.ts)
					if ((attr === "src" || attr === "href") && deferredSources.has(this))
						return deferredSources.get(this);
					if (["src", "data", "href", "action", "formaction"].includes(attr)) {
						// A data: address as written, normalised as the browser does: read back from
						// the proxied address, its <, > and " came back percent-encoded.
						const written = client.natives.call(
							"Element.prototype.getAttribute",
							this,
							`scramjet-attr-${attr}`
						);
						if (typeof written === "string") {
							const url = written
								.replace(/^[\x00-\x20]+|[\x00-\x20]+$/g, "")
								.replace(/[\t\n\r]/g, "");
							if (/^data:/i.test(url)) return "data:" + url.slice(5);
						}

						const value = descriptor.get.call(this);
						// a written script loaded through a carrier (client/dom/document.ts)
						const source = blobSources.get(value);

						return unrewriteUrl(source === undefined ? value : source, client.context);
					}
					// srcset and imageSrcset read back the attribute as written; they returned
					// the rewritten addresses, which lazy loaders copy and compare (ESPN's images).
					// ping too: the browser gives the attribute back unchanged.
					if (
						attr === "srcset" ||
						attr === "imagesrcset" ||
						attr === "ping" ||
						attr === "target" ||
						attr === "formtarget"
					) {
						return this.getAttribute(attr) ?? "";
					}

					return descriptor.get.call(this);
				},

				set(value) {
					// if (
					// 	this.tagName === "IFRAME" &&
					// 	attr === "src" &&
					// 	value === "about:blank"
					// ) {
					// 	this.setAttribute("srcdoc", "");
					// 	return;
					// }
					// Scramjet's setAttribute, not the page's: a page that wraps both setAttribute
					// and this setter, each calling the other's original (Osano on Flickr), would
					// otherwise loop forever. The browser's own setter calls neither.
					return Reflect.apply(ownSetAttribute, this, [attr, value]);
				},
			});
		}
	}

	client.Trap("HTMLImageElement.prototype.currentSrc", {
		get(ctx) {
			const currentSrc = ctx.get() as string;
			if (!currentSrc) return currentSrc;
			// loaded from a blob fetched through a carrier (carrier.ts)
			if (deferredSources.has(ctx.this)) return deferredSources.get(ctx.this);
			return unrewriteUrl(currentSrc, client.context);
		},
	});

	// note that href is not here
	const urlprops = [
		"protocol",
		"hash",
		"host",
		"hostname",
		"origin",
		"pathname",
		"port",
		"search",
	];
	for (const prop of urlprops) {
		for (const i in urlinterfaces) {
			const target = urlinterfaces[i];
			const desc = originalhrefs[i];
			client.RawTrap(target, prop, {
				get(ctx) {
					const href = desc.get.call(ctx.this);
					if (!href) return href;

					// Browsers never throw here: an address that is not a URL gives "" (":" for the
					// protocol). Matomo (OpenStreetMap) reads hostname of a link it made and broke off.
					let url: URL;
					try {
						url = new URL(unrewriteUrl(href, client.context));
					} catch {
						return prop === "protocol" ? ":" : "";
					}

					return url[prop];
				},
			});
		}
	}

	client.Trap("Node.prototype.baseURI", {
		get(ctx) {
			// Only the browser's own functions: pages that wrap querySelector or getAttribute
			// and read baseURI inside (Osano on Flickr) otherwise recursed without end.
			const node = ctx.this as Node;
			const doc = client.box.instanceof(node, "Document")
				? (node as Document)
				: (client.descriptors.get("Node.prototype.ownerDocument", node) as Document);
			const base = doc
				? (client.natives.call(
						"Document.prototype.querySelector",
						doc,
						"base[href]"
					) as HTMLBaseElement | null)
				: null;

			if (base) {
				const getAttribute = (name: string) =>
					client.natives.call("Element.prototype.getAttribute", base, name);
				// the page's own value, kept aside when the attribute was rewritten
				const href = getAttribute("scramjet-attr-href") ?? getAttribute("href");
				if (href) return new URL(href, client.url.href).href;
			}

			return client.url.href;
		},
		set() {
			return false;
		},
	});

	client.Proxy("Element.prototype.getAttribute", {
		apply(ctx) {
			const [name] = ctx.args;
			// anything but a string is the browser's to convert or refuse
			if (typeof name !== "string") return;

			if (name.startsWith("scramjet-attr")) {
				return ctx.return(null);
			}

			if (
				client.natives.call(
					"Element.prototype.hasAttribute",
					ctx.this,
					`scramjet-attr-${name}`
				)
			) {
				// Chrome hides a nonce from getAttribute once the site's Content-Security-Policy
				// applies (the proxy drops that policy); the nonce property has it (below).
				if (name.toLowerCase() === "nonce") return ctx.return("");
				const attrib = ctx.fn.call(ctx.this, `scramjet-attr-${name}`);
				if (attrib === null) return ctx.return("");

				return ctx.return(attrib);
			}

			// A style attribute written through element.style (cssText, a property) holds the
			// proxied addresses and keeps no copy of the page's: read back with the page's.
			if (name.toLowerCase() === "style") {
				const value = ctx.call() as string | null;
				ctx.return(
					value && value.includes(client.context.prefix.origin)
						? unrewriteCss(value, client.context)
						: value
				);
			}
		},
	});

	client.Proxy("Element.prototype.getAttributeNames", {
		apply(ctx) {
			const attrNames = ctx.call() as string[];
			const cleaned = attrNames.filter(
				(attr) => !attr.startsWith("scramjet-attr")
			);

			ctx.return(cleaned);
		},
	});

	client.Proxy("Element.prototype.getAttributeNode", {
		apply(ctx) {
			if (String(ctx.args[0]).startsWith("scramjet-attr"))
				return ctx.return(null);
		},
	});

	client.Proxy("Element.prototype.hasAttribute", {
		apply(ctx) {
			const name = String(ctx.args[0]);
			if (name.startsWith("scramjet-attr")) return ctx.return(false);
			// An attribute the rewriting took out (nonce, an iframe's csp) is still the page's.
			const has = ctx.call();
			ctx.return(
				has ||
					client.natives.call(
						"Element.prototype.hasAttribute",
						ctx.this,
						`scramjet-attr-${name}`
					)
			);
		},
	});

	client.Proxy("Element.prototype.setAttribute", {
		apply(ctx) {
			let [name, value] = ctx.args;
			const tagName = ctx.this.tagName.toLowerCase();

			if (value != null) value = String(value);
			ctx.args[1] = value;

			const ruleList = htmlRules.find((rule) => {
				const r = rule[name.toLowerCase()];
				if (!r) return false;
				if (r === "*") return true;
				if (typeof r === "function") return false; // this can't happen but ts

				return r.includes(tagName);
			});

			if (ruleList) {
				const ret = ruleList.fn(
					value,
					client.context,
					client.meta,
					collectAttributeMap(client, ctx.this, name, value)
				);
				if (ret == null) {
					client.natives.call(
						"Element.prototype.removeAttribute",
						ctx.this,
						name
					);
					ctx.fn.call(ctx.this, `scramjet-attr-${name}`, value);
					ctx.return(undefined);

					return;
				}
				ctx.args[1] = ret;
				ctx.fn.call(ctx.this, `scramjet-attr-${ctx.args[0]}`, value);
				cancelDeferred(ctx.this);
				if (
					deferLoad(
						client,
						ctx.this,
						tagName,
						name.toLowerCase(),
						ret,
						collectAttributeMap(client, ctx.this, name, value)
					)
				) {
					ctx.return(undefined);
				}
			}
		},
	});

	// as installed above, for the property setters (see there)
	const ownSetAttribute = self.Element.prototype.setAttribute;

	// An Attr node set on an element: rewritten like setAttribute, before it is attached.
	client.Proxy("Element.prototype.setAttributeNode", {
		apply(ctx) {
			const attr = ctx.args[0] as Attr;
			if (!attr || typeof attr.name !== "string" || attr.ownerElement) return;
			const name = attr.name;
			const value = attr.value;

			const ruleList = htmlRules.find((rule) => {
				const r = rule[name.toLowerCase()];
				if (!r) return false;
				if (r === "*") return true;
				if (typeof r === "function") return false;

				return r.includes(ctx.this.tagName.toLowerCase());
			});
			if (!ruleList) return;

			const rewritten = ruleList.fn(
				value,
				client.context,
				client.meta,
				collectAttributeMap(client, ctx.this, name, value)
			);
			if (rewritten == null) return;
			attr.value = rewritten;
			client.natives.call(
				"Element.prototype.setAttribute",
				ctx.this,
				`scramjet-attr-${name}`,
				value
			);
		},
	});

	client.Proxy("Element.prototype.setAttributeNS", {
		apply(ctx) {
			// TODO: this could leak by like calling stringify twice or some dumb shit lol
			const name = String(ctx.args[1]);
			const value = String(ctx.args[2]);

			const ruleList = htmlRules.find((rule) => {
				// Namespaced names (xlink:href on an SVG <use>) count as their local name too.
				const lower = String(name).toLowerCase();
				const r = rule[lower] ?? rule[lower.replace(/^xlink:/, "")];
				if (!r) return false;
				if (r === "*") return true;
				if (typeof r === "function") return false; // this can't happen but ts

				return r.includes(ctx.this.tagName.toLowerCase());
			});

			if (ruleList) {
				ctx.args[2] = ruleList.fn(
					value,
					client.context,
					client.meta,
					collectAttributeMap(client, ctx.this, name, value)
				);
				client.natives.call(
					"Element.prototype.setAttribute",
					ctx.this,
					`scramjet-attr-${ctx.args[1]}`,
					value
				);
			}
		},
	});

	// this is separate from the regular href handlers because it returns an SVGAnimatedString
	client.Trap("SVGAnimatedString.prototype.baseVal", {
		get(ctx) {
			const href = ctx.get() as string;
			if (!href) return href;

			return unrewriteUrl(href, client.context);
		},
		set(ctx, val: string) {
			ctx.set(client.rewriteUrl(val));
		},
	});
	client.Trap("SVGAnimatedString.prototype.animVal", {
		get(ctx) {
			const href = ctx.get() as string;
			if (!href) return href;

			return unrewriteUrl(href, client.context);
		},
		// it has no setter
	});

	client.Proxy("Element.prototype.removeAttribute", {
		apply(ctx) {
			const name = String(ctx.args[0]);
			if (name.startsWith("scramjet-attr")) return ctx.return(undefined);
			if (
				client.natives.call("Element.prototype.hasAttribute", ctx.this, name)
			) {
				ctx.fn.call(ctx.this, `scramjet-attr-${ctx.args[0]}`);
			}
		},
	});

	client.Proxy("Element.prototype.toggleAttribute", {
		apply(ctx) {
			const name = String(ctx.args[0]);
			if (name.startsWith("scramjet-attr")) return ctx.return(false);
			if (
				client.natives.call("Element.prototype.hasAttribute", ctx.this, name)
			) {
				ctx.fn.call(ctx.this, `scramjet-attr-${ctx.args[0]}`);
			}
		},
	});

	// innerHTML/outerHTML as the page wrote it, serialized by the browser itself. Markup that
	// holds nothing of the proxy's is the browser's own string. Otherwise a copy goes into an
	// inert document (a template's: nothing in it loads or runs, custom elements stay plain),
	// gets its original attributes, script code and style text back, and the browser serializes
	// that. Parsing the browser's string with htmlparser2 and writing it out again, as before,
	// changed it: `disabled` for disabled="", <use/> for <use></use>, comments in a <textarea>
	// escaped, and <style> text and inline script markers came out as the proxy had them.
	let inertDocument: Document | null = null;
	const proxyMark = client.context.prefix.origin;
	const needsPageHtml = (html: string) =>
		typeof html === "string" &&
		(html.includes("scramjet-attr-") || html.includes(proxyMark));
	const restoreOriginals = (root: Node) => {
		const doc = root.ownerDocument || (root as Document);
		const walker = client.natives.call(
			"Document.prototype.createTreeWalker",
			doc,
			root,
			1 /* SHOW_ELEMENT */
		) as TreeWalker;
		for (let el = walker.currentNode as Element | null; el; el = walker.nextNode() as Element | null) {
			if (el.nodeType !== 1) continue;
			const map = client.descriptors.get("Element.prototype.attributes", el) as NamedNodeMap;
			const own: [string, string][] = [];
			for (let i = 0; i < map.length; i++) {
				const attr = map[i];
				if (attr.name.startsWith("scramjet-attr-"))
					own.push([attr.name, client.descriptors.get("Attr.prototype.value", attr) as string]);
			}
			for (const [name, value] of own) {
				const real = name.slice("scramjet-attr-".length);
				if (real === "script-source-src") {
					try {
						client.descriptors.set("Node.prototype.textContent", el, base64ToText(value));
					} catch {}
				} else {
					try {
						client.natives.call("Element.prototype.setAttribute", el, real, value);
					} catch {}
				}
				client.natives.call("Element.prototype.removeAttribute", el, name);
			}
			if (el.localName === "style") {
				for (const child of Array_from(el.childNodes) as any[]) {
					if (child.nodeType !== 3) continue;
					const data = client.descriptors.get("CharacterData.prototype.data", child) as string;
					const css = unrewriteCss(data, client.context);
					if (css !== data) client.descriptors.set("CharacterData.prototype.data", child, css);
				}
			} else if (el.localName === "template") {
				const content = client.descriptors.get("HTMLTemplateElement.prototype.content", el);
				if (content) restoreOriginals(content);
			}
		}
	};
	/** A copy of `node` in the inert document with the page's own markup back; null if it cannot be made. */
	const pageCopy = (node: Node): Node | null => {
		try {
			const owner = (client.box.instanceof(node, "Document") ? node : node.ownerDocument) as Document;
			if (!owner || client.descriptors.get("Document.prototype.contentType", owner) !== "text/html")
				return null;
			if (!inertDocument) {
				const template = client.natives.call(
					"Document.prototype.createElement",
					self.document,
					"template"
				) as HTMLTemplateElement;
				inertDocument = (
					client.descriptors.get("HTMLTemplateElement.prototype.content", template) as DocumentFragment
				).ownerDocument;
			}
			const importNode = (n: Node) =>
				client.natives.call("Document.prototype.importNode", inertDocument!, n, true) as Node;
			let copy: Node;
			if (client.box.instanceof(node, "ShadowRoot")) {
				copy = client.natives.call("Document.prototype.createElement", inertDocument!, "div") as Element;
				for (const child of Array_from(node.childNodes))
					client.natives.call("Node.prototype.appendChild", copy, importNode(child as Node));
			} else if (client.box.instanceof(node, "Element") || client.box.instanceof(node, "DocumentFragment")) {
				copy = importNode(node);
			} else {
				return null;
			}
			restoreOriginals(copy);

			return copy;
		} catch {
			return null;
		}
	};
	// <noscript> holds its content as text while scripts run, and Scramjet rewrote the markup in
	// that text; the old way (htmlparser2) parses and restores it.
	const hasRawMarkup = (html: string) => /<(noscript|noembed|noframes)[\s>]/i.test(html);
	/** `node`'s markup as the page wrote it; null when this way cannot give it (then: the old one). */
	const pageHtml = (node: Node, native: string, outer: boolean): string | null => {
		if (!needsPageHtml(native)) return native;
		if (hasRawMarkup(native)) return null;
		const copy = pageCopy(node);
		if (!copy) return null;

		return client.descriptors.get(
			outer ? "Element.prototype.outerHTML" : "Element.prototype.innerHTML",
			copy
		) as string;
	};

	// XMLSerializer (SVG and chart exports, editors saving their content): the page's own
	// addresses, as innerHTML gives them.
	client.Proxy("XMLSerializer.prototype.serializeToString", {
		apply(ctx) {
			const xml = ctx.call() as string;
			if (!needsPageHtml(xml) || hasRawMarkup(xml)) return ctx.return(xml);
			const copy = pageCopy(ctx.args[0] as Node);
			ctx.return(copy ? (ctx.fn.call(ctx.this, copy) as string) : xml);
		},
	});

	// <base href> stays as the page wrote it, and its href property resolved that against the
	// proxy's own address ("/sub/" read back as http://<proxy>/sub/): single-page apps that take
	// their root from it made links and history entries outside the site.
	client.Trap("HTMLBaseElement.prototype.href", {
		get(ctx) {
			const base = ctx.this as Element;
			const value =
				(client.natives.call("Element.prototype.getAttribute", base, "scramjet-attr-href") as string | null) ??
				(client.natives.call("Element.prototype.getAttribute", base, "href") as string | null) ??
				"";
			try {
				return new URL(value, client.url.href).href;
			} catch {
				return value;
			}
		},
	});

	// Web components write their content through ShadowRoot.innerHTML.
	client.Trap("ShadowRoot.prototype.innerHTML", {
		set(ctx, value: string) {
			if (value === null) return;
			const html = String(value);
			const host = (ctx.this as ShadowRoot).host;
			let newval: string;
			try {
				newval = rewriteHtml(html, client.context, client.meta, {
					loadScripts: false,
					inline: true,
					source: client.url.href,
					apisource: "set ShadowRoot.prototype.innerHTML",
					foreignContext: foreignContextForElement(client, host),
				});
			} catch {
				newval = html;
			}
			ctx.set(newval);
			htmlMadeFrames(html, ctx.this);
		},
		get(ctx) {
			const native = ctx.get() as string;
			return (
				pageHtml(ctx.this as Node, native, false) ??
				unrewriteHtml(
					native,
					foreignContextForElement(client, (ctx.this as ShadowRoot).host),
					client.context
				)
			);
		},
	});

	client.Trap("Element.prototype.innerHTML", {
		set(ctx, value: string) {
			// null specifically becomes "" and not "null". undefined does not
			if (value === null) return;
			const html = String(value);
			let newval;
			const scriptBlockType = client.box.instanceof(
				ctx.this,
				"HTMLScriptElement"
			)
				? scriptBlockTypeForElement(client, ctx.this)
				: null;
			if (
				client.box.instanceof(ctx.this, "HTMLScriptElement") &&
				isScriptType(scriptBlockType)
			) {
				newval = rewriteJs(
					html,
					"(anonymous script element)",
					client.context,
					client.meta,
					isModuleScriptType(scriptBlockType)
				);
				client.natives.call(
					"Element.prototype.setAttribute",
					ctx.this,
					"scramjet-attr-script-source-src",
					bytesToBase64(TextEncoder_encode(newval))
				);
			} else if (client.box.instanceof(ctx.this, "HTMLStyleElement")) {
				newval = rewriteCss(html, client.context, client.meta);
			} else {
				try {
					newval = rewriteHtml(html, client.context, client.meta, {
						loadScripts: false,
						inline: true,
						source: client.url.href,
						apisource: "set Element.prototype.innerHTML",
						foreignContext: foreignContextForElement(client, ctx.this),
					});
				} catch {
					newval = html;
				}
			}

			ctx.set(newval);
			htmlMadeFrames(html, ctx.this);
		},
		get(ctx) {
			if (client.box.instanceof(ctx.this, "HTMLScriptElement")) {
				const scriptSource = client.natives.call(
					"Element.prototype.getAttribute",
					ctx.this,
					"scramjet-attr-script-source-src"
				);

				if (scriptSource) {
					return base64ToText(scriptSource);
				}

				return ctx.get();
			}
			if (client.box.instanceof(ctx.this, "HTMLStyleElement")) {
				// with the page's addresses, as textContent gives them
				return unrewriteCss(ctx.get() as string, client.context);
			}

			const native = ctx.get() as string;
			return (
				pageHtml(ctx.this as Node, native, false) ??
				unrewriteHtml(native, foreignContextForElement(client, ctx.this), client.context)
			);
		},
	});

	const rewriteTextForElement = (element: Element, value: string) => {
		const scriptBlockType = client.box.instanceof(element, "HTMLScriptElement")
			? scriptBlockTypeForElement(client, element)
			: null;

		if (
			client.box.instanceof(element, "HTMLScriptElement") &&
			isScriptType(scriptBlockType)
		) {
			const newval: string = rewriteJs(
				value,
				"(anonymous script element)",
				client.context,
				client.meta,
				isModuleScriptType(scriptBlockType)
			) as string;
			client.natives.call(
				"Element.prototype.setAttribute",
				element,
				"scramjet-attr-script-source-src",
				bytesToBase64(TextEncoder_encode(value))
			);

			return newval;
		} else if (
			client.box.instanceof(element, "HTMLScriptElement") &&
			String(scriptBlockType).toLowerCase() === "importmap"
		) {
			// An import map added by a script (CrazyGames): its addresses in proxied form, as
			// for import maps in the page's HTML; reading it back gives the page's own text.
			client.natives.call(
				"Element.prototype.setAttribute",
				element,
				"scramjet-attr-script-source-src",
				bytesToBase64(TextEncoder_encode(value))
			);

			return rewriteImportMap(value, client.context, client.meta);
		} else if (client.box.instanceof(element, "HTMLStyleElement")) {
			return rewriteCss(value, client.context, client.meta);
		} else {
			return value;
		}
	};
	const getTextForElement = (element: Element, text: string) => {
		if (client.box.instanceof(element, "HTMLScriptElement")) {
			const scriptSource = client.natives.call(
				"Element.prototype.getAttribute",
				element,
				"scramjet-attr-script-source-src"
			);
			if (scriptSource) return base64ToText(scriptSource);
			return text;
		}
		if (client.box.instanceof(element, "HTMLStyleElement")) {
			return unrewriteCss(text, client.context);
		}
		return text;
	};

	client.Trap(
		[
			"Node.prototype.textContent",
			"HTMLScriptElement.prototype.textContent",
			// Code set through script.text otherwise ran unrewritten.
			"HTMLScriptElement.prototype.text",
		],
		{
			set(ctx, value) {
				const text = String(value);
				return ctx.set(rewriteTextForElement(ctx.this, text));
			},
			get(ctx) {
				return getTextForElement(ctx.this, ctx.get());
			},
		}
	);

	// CSS added to a <style> as text nodes (how CSS-in-JS libraries add rules) is rewritten
	// like textContent; otherwise its fonts and images load from the proxy's own origin.
	const rewriteStyleText = (parent: Node, node: unknown) => {
		if (
			client.box.instanceof(parent, "HTMLStyleElement") &&
			client.box.instanceof(node, "Text")
		) {
			(node as Text).data = rewriteTextForElement(
				parent as Element,
				(node as Text).data
			);
		}
	};
	// A frame's window can be reached without contentWindow, through window.frames[name] or
	// window[0]. Anti-adblock scripts take a clean fetch from a hidden blank frame that way
	// (dualping on AP News), and with no proxy code in that frame its requests went straight
	// to the site, with the visitor's address. So every frame is set up the moment it is in
	// the page, however it got there.
	const framesIn = (node: any): any[] | null => {
		if (!node || typeof node !== "object") return null;
		try {
			if (
				client.box.instanceof(node, "HTMLIFrameElement") ||
				client.box.instanceof(node, "HTMLFrameElement")
			)
				return [node];
			let query: string | null = null;
			if (client.box.instanceof(node, "Element"))
				query = "Element.prototype.querySelectorAll";
			else if (client.box.instanceof(node, "DocumentFragment"))
				query = "DocumentFragment.prototype.querySelectorAll";
			if (!query || !node.firstElementChild) return null;
			const found = client.natives.call(query, node, "iframe,frame");

			return found.length ? Array_from(found) : null;
		} catch {
			return null;
		}
	};
	const setUpFrames = (frames: any[] | null) => {
		if (!frames) return;
		for (const el of frames) {
			try {
				if (!el.isConnected) continue;
				const win = client.descriptors.get(
					client.box.instanceof(el, "HTMLFrameElement")
						? "HTMLFrameElement.prototype.contentWindow"
						: "HTMLIFrameElement.prototype.contentWindow",
					el
				);
				if (win && !(SCRAMJETCLIENT in win)) client.init.hookSubcontext(win, el);
			} catch {
				// a frame of another origin: nothing to set up
			}
		}
	};
	/** Runs the native insertion, then sets up the frames the inserted nodes brought. */
	const insertingFrames = (ctx: any, nodes: any[]) => {
		for (const node of nodes) if (foreign(node)) rewriteForeignTree(node);
		let frames: any[] | null = null;
		for (const node of nodes) {
			const found = framesIn(node);
			if (found) frames = frames ? frames.concat(found) : found;
		}
		if (!frames) return;
		const result = ctx.call();
		setUpFrames(frames);
		ctx.return(result);
	};
	/** After HTML was set as text: the frames it made under root. */
	const htmlMadeFrames = (html: string, root: any) => {
		if (!/<i?frame/i.test(html) || !root) return;
		setUpFrames(framesIn(root));
	};

	client.Proxy(["Node.prototype.appendChild", "Node.prototype.insertBefore"], {
		apply(ctx) {
			rewriteStyleText(ctx.this, ctx.args[0]);
			insertingFrames(ctx, [ctx.args[0]]);
		},
	});
	client.Proxy("Node.prototype.replaceChild", {
		apply(ctx) {
			insertingFrames(ctx, [ctx.args[0]]);
		},
	});
	client.Proxy(["Element.prototype.append", "Element.prototype.prepend"], {
		apply(ctx) {
			if (client.box.instanceof(ctx.this, "HTMLStyleElement")) {
				for (let i = 0; i < ctx.args.length; i++) {
					const arg = ctx.args[i];
					if (typeof arg === "string") {
						ctx.args[i] = rewriteTextForElement(ctx.this, arg);
					} else {
						rewriteStyleText(ctx.this, arg);
					}
				}
			}
			insertingFrames(ctx, ctx.args);
		},
	});
	client.Proxy(
		[
			"Element.prototype.after",
			"Element.prototype.before",
			"Element.prototype.replaceWith",
			"Element.prototype.replaceChildren",
		],
		{
			apply(ctx) {
				insertingFrames(ctx, ctx.args);
			},
		}
	);
	client.Proxy("Element.prototype.insertAdjacentElement", {
		apply(ctx) {
			insertingFrames(ctx, [ctx.args[1]]);
		},
	});
	client.Trap(
		[
			"HTMLElement.prototype.innerText",
			"HTMLScriptElement.prototype.innerText",
		],
		{
			set(ctx, value: string) {
				const text = String(value);
				return ctx.set(rewriteTextForElement(ctx.this, text));
			},
			get(ctx) {
				return getTextForElement(ctx.this, ctx.get());
			},
		}
	);

	client.Trap("Element.prototype.outerHTML", {
		set(ctx, value: string) {
			const html = String(value);
			const parent = (ctx.this as Element).parentNode;
			ctx.set(
				rewriteHtml(html, client.context, client.meta, {
					loadScripts: false,
					inline: true,
					source: client.url.href,
					apisource: "set Element.prototype.outerHTML",
					foreignContext: insideForeignContext(client, ctx.this),
				})
			);
			htmlMadeFrames(html, parent);
		},
		get(ctx) {
			const native = ctx.get() as string;
			return (
				pageHtml(ctx.this as Node, native, true) ??
				unrewriteHtml(native, insideForeignContext(client, ctx.this), client.context)
			);
		},
	});

	client.Proxy("Element.prototype.setHTMLUnsafe", {
		apply(ctx) {
			const html = String(ctx.args[0]);
			ctx.args[0] = rewriteHtml(html, client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "set Element.prototype.setHTMLUnsafe",
				foreignContext: foreignContextForElement(client, ctx.this),
			});
			ctx.return(ctx.call());
			htmlMadeFrames(html, ctx.this);
		},
	});

	client.Proxy("ShadowRoot.prototype.setHTMLUnsafe", {
		apply(ctx) {
			const html = String(ctx.args[0]);
			ctx.args[0] = rewriteHtml(html, client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "set ShadowRoot.prototype.setHTMLUnsafe",
				foreignContext: foreignContextForElement(client, (ctx.this as ShadowRoot).host),
			});
			ctx.return(ctx.call());
			htmlMadeFrames(html, ctx.this);
		},
	});

	client.Proxy(["Document.prototype.importNode", "Document.prototype.adoptNode"], {
		apply(ctx) {
			if (!foreign(ctx.args[0])) return;
			const node = ctx.call();
			rewriteForeignTree(node);
			ctx.return(node);
		},
	});

	client.Proxy("Element.prototype.getHTML", {
		apply(ctx) {
			ctx.return(unrewriteHtml(ctx.call(), undefined, client.context));
		},
	});

	client.Proxy("Element.prototype.insertAdjacentHTML", {
		apply(ctx) {
			const html = String(ctx.args[1]);
			ctx.args[1] = rewriteHtml(html, client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "set Element.prototype.insertAdjacentHTML",
				foreignContext: foreignContextForElement(client, ctx.this),
			});
			const where = String(ctx.args[0]).toLowerCase();
			const root =
				where === "beforebegin" || where === "afterend"
					? (ctx.this as Element).parentNode
					: ctx.this;
			ctx.return(ctx.call());
			htmlMadeFrames(html, root);
		},
	});

	// TODO: this needs to be done for all insert methods
	// client.Proxy(["Element.prototype.appendChild", "Element.prototype.append"], {
	// 	apply(ctx) {
	// 		if (ctx.this instanceof self.HTMLStyleElement) {
	// 			for (const node of ctx.args) {
	// 				if (node instanceof self.Text) {
	// 					node.data = rewriteCss(
	// 						ctx.args[0].data,
	// 						client.context,
	// 						client.meta
	// 					);
	// 				}
	// 			}
	// 		} else if (ctx.this instanceof self.HTMLScriptElement) {
	// 			for (const node of ctx.args) {
	// 				if (node instanceof self.Text) {
	// 					const newval: string = rewriteJs(
	// 						node.data,
	// 						"(anonymous script element)",
	// 						client.context,
	// 						client.meta
	// 					) as string;
	// 					client.natives.call(
	// 						"Element.prototype.setAttribute",
	// 						ctx.this,
	// 						"scramjet-attr-script-source-src",
	// 						bytesToBase64(encoder.encode(newval))
	// 					);
	// 					node.data = newval;
	// 				}
	// 			}
	// 		}
	// 	},
	// });

	client.Proxy("Audio", {
		construct(ctx) {
			if (ctx.args[0]) ctx.args[0] = client.rewriteUrl(ctx.args[0]);
		},
	});
	client.Proxy("Text.prototype.appendData", {
		apply(ctx) {
			const text = String(ctx.args[0]);
			const parent = client.natives.call(
				"Node.prototype.parentElement",
				ctx.this
			);
			ctx.args[0] = rewriteTextForElement(parent, text);
		},
	});

	client.Proxy("Text.prototype.insertData", {
		apply(ctx) {
			const text = String(ctx.args[1]);
			const parent = client.natives.call(
				"Node.prototype.parentElement",
				ctx.this
			);
			ctx.args[1] = rewriteTextForElement(parent, text);
		},
	});

	client.Proxy("Text.prototype.replaceData", {
		apply(ctx) {
			const text = String(ctx.args[2]);
			const parent = client.natives.call(
				"Node.prototype.parentElement",
				ctx.this
			);
			ctx.args[2] = rewriteTextForElement(parent, text);
		},
	});

	client.Trap("Text.prototype.wholeText", {
		get(ctx) {
			const parent = client.natives.call(
				"Node.prototype.parentElement",
				ctx.this
			);
			return getTextForElement(parent, ctx.get());
		},
		set(ctx, v) {
			const text = String(v);
			const parent = client.natives.call(
				"Node.prototype.parentElement",
				ctx.this
			);
			return ctx.set(rewriteTextForElement(parent, text));
		},
	});

	client.Proxy("HTMLAnchorElement.prototype.toString", {
		apply(ctx) {
			const href = ctx.call();
			if (!href) return href;
			return ctx.return(unrewriteUrl(href, client.context));
		},
	});

	client.Trap(
		[
			"HTMLIFrameElement.prototype.contentWindow",
			"HTMLFrameElement.prototype.contentWindow",
			"HTMLObjectElement.prototype.contentWindow",
			"HTMLEmbedElement.prototype.contentWindow",
		],
		{
			get(ctx) {
				const realwin = ctx.get() as Window;
				if (!realwin) return realwin;

				try {
					if (!(SCRAMJETCLIENT in realwin)) {
						// hook the iframe before the client can start to steal globals out of it
						client.init.hookSubcontext(realwin, ctx.this);
					}
				} catch {
					// cross-origin iframe, can't do anything here
					return realwin;
				}

				return realwin;
			},
		}
	);

	client.Trap(
		[
			"HTMLIFrameElement.prototype.contentDocument",
			"HTMLFrameElement.prototype.contentDocument",
			"HTMLObjectElement.prototype.contentDocument",
			"HTMLEmbedElement.prototype.contentDocument",
		],
		{
			get(ctx) {
				const realwin = client.descriptors.get(
					`${ctx.this.constructor.name}.prototype.contentWindow`,
					ctx.this
				);
				if (!realwin) return realwin;

				if (!(SCRAMJETCLIENT in realwin)) {
					client.init.hookSubcontext(realwin, ctx.this);
				}

				return realwin.document;
			},
		}
	);

	client.Proxy(
		[
			"HTMLIFrameElement.prototype.getSVGDocument",
			"HTMLObjectElement.prototype.getSVGDocument",
			"HTMLEmbedElement.prototype.getSVGDocument",
		],
		{
			apply(ctx) {
				const doc = ctx.call();
				if (doc) {
					// we trap the contentDocument, this is really the scramjet version
					return ctx.return(ctx.this.contentDocument);
				}
			},
		}
	);

	// Elements parsed by the browser outside the page's hooks (XHR documents, XML from
	// DOMParser) carry the site's addresses as written. Each attribute not rewritten yet (no
	// scramjet-attr-* copy) is set again through the hooked setAttribute(NS), which rewrites
	// the ones that load something. Marfeel's consent box on 20minutos fetched its HTML as an
	// XHR document and put those elements into the page: their pictures loaded straight from
	// the site.
	const rewriteForeignTree = (root: any) => {
		if (!root || typeof root !== "object") return;
		const fix = (el: Element) => {
			const map = client.descriptors.get("Element.prototype.attributes", el) as NamedNodeMap;
			if (!map) return;
			const todo: [string | null, string, string][] = [];
			for (let j = 0; j < map.length; j++) {
				const attr = map[j];
				const name = attr.name;
				if (name.startsWith("scramjet-attr-")) continue;
				if (client.natives.call("Element.prototype.hasAttribute", el, "scramjet-attr-" + name)) continue;
				todo.push([attr.namespaceURI, name, attr.value]);
			}
			for (const [ns, name, value] of todo) {
				try {
					if (ns) el.setAttributeNS(ns, name, value);
					else el.setAttribute(name, value);
				} catch {
					// a name setAttribute refuses: leave it
				}
			}
			// a <style>'s text: read back as written, set again rewritten, one text node at a
			// time. Setting textContent would also drop its other children: Lit keeps comment
			// markers in a <style> with bindings, and every such template then failed
			// ("reading 'nextSibling'") when it was cloned (importNode) to render.
			if (el.localName === "style") {
				for (const child of Array_from(el.childNodes) as any[]) {
					if (!client.box.instanceof(child, "Text")) continue;
					const data = client.descriptors.get("CharacterData.prototype.data", child) as string;
					const fixed = rewriteTextForElement(el, getTextForElement(el, data));
					if (fixed !== data) client.descriptors.set("CharacterData.prototype.data", child, fixed);
				}
			}
		};
		try {
			if (client.box.instanceof(root, "Element")) fix(root);
			const all =
				client.box.instanceof(root, "Document") || client.box.instanceof(root, "DocumentFragment") || client.box.instanceof(root, "Element")
					? root.getElementsByTagName
						? root.getElementsByTagName("*")
						: root.querySelectorAll("*")
					: [];
			for (let i = 0; i < all.length; i++) fix(all[i]);
		} catch {
			// leave it as it is
		}
	};
	/** Nodes from another document (not created through this page's hooked functions). */
	const foreign = (node: any) => {
		try {
			return !!node && typeof node === "object" && node.ownerDocument && node.ownerDocument !== self.document;
		} catch {
			return false;
		}
	};

	client.Proxy("DOMParser.prototype.parseFromString", {
		apply(ctx) {
			const html = String(ctx.args[0]);
			const mime = String(ctx.args[1]);
			if (!isHtmlMimeType(mime)) {
				const doc = ctx.call() as Document;
				rewriteForeignTree(doc);
				ctx.return(doc);

				return;
			}
			ctx.args[0] = rewriteHtml(html, client.context, client.meta, {
				loadScripts: false,
				inline: true,
				source: client.url.href,
				apisource: "DOMParser.prototype.parseFromString",
			});
		},
	});
}
